import { SupportedLanguage } from '../../core/i18n';

export interface SignInResultDto {
    accessToken: string;
    idToken: string;
    refreshToken: string;
}
export interface CompleteSignUpDto {
    clientId?: string;
    id: string;
    email: string;
    oobCode: string;
}

export enum IdpOauth2LoginCodeChallengeMethod {
    PLAIN = 'plain',
    S256 = 'S256',
}

export enum IdpOauth2LoginResponseType {
    CODE = 'code',
}

export enum OobRequestType {
    EMAIL_SIGNIN = 'EMAIL_SIGNIN',
    PASSWORD_RESET = 'PASSWORD_RESET',
}

export enum IdpOauth2LoginClaim {
    SUB = 'sub',
    EMAIL = 'email',
    EMAIL_VERIFIED = 'email_verified',
}

export enum ErrorKeyIdpOauth {
    INVALID_REQUEST = 'invalid_request',
}
export enum IdpOauth2LoginScope {
    OPENID = 'openid',
    EMAIL = 'email', // todo what if a user signs in via jwt?
    PROFILE = 'profile',
}

export interface Oauth2ClientDto {
    name: string;
}

export interface ResetPasswordDto {
    clientId?: string;
    email: string;
    oobCode: string;
    password: string;
}

export interface SendOobCodeDto {
    clientId?: string;
    requestType: OobRequestType;
    email: string;
    url: string;
}

export interface SignInWithEmailDto {
    clientId?: string;
    email: string;
    oobCode: string;
}

export interface SignInWithPasswordDto {
    clientId?: string;
    email: string;
    password: string;
}
export interface SignupUserAttributesDto {
    language: SupportedLanguage;
    firstName: string;
    lastName: string;
}

export interface SignupUserDto {
    clientId?: string;
    password?: string;
    email: string;
    userAttributes: SignupUserAttributesDto;
    url: string;
}

export interface SignupUserResultDto {
    /**
     * If set, a code has been send to the email address used to create the account with this length
     */
    codeLength: number;
    id: string;
}

export interface Oauth2LoginDto {
    methods: IdpOauth2LoginMethod[];
    action: IdpOauth2AuthorizeAction;
    expiresAt: string;
    client: Oauth2ClientDto;
    session: Oauth2SessionDto | null;
}

export interface Oauth2TokenDto {
    sub: string;
    email?: string;
    email_verified?: boolean;
    aud: string;
    azp: string;
    exp: number;
    family_name?: string;
    given_name?: string;
    iat: number;
    iss: string;
    name: string;
    nonce: string;
    token_use: string;
}

export interface Oauth2SessionDto {
    expiresAt: string;
    firstName: string;
}

export interface Oauth2ConfigDto {
    response_types_supported: IdpOauth2LoginResponseType;
    scopes_supported: IdpOauth2LoginScope[];
    claims_supported: IdpOauth2LoginClaim[];
    grant_types_supported: IdpOauth2GrantType[];
    code_challenge_methods_supported: IdpOauth2LoginCodeChallengeMethod[];
    issuer: string;
    authorization_endpoint: string;
    token_endpoint: string;
    end_session_endpoint: string;
    jwks_uri: string;
    userinfo_endpoint: string;
}

export interface Oauth2AuthorizeQueryDto {
    response_type: IdpOauth2LoginResponseType;
    code_challenge_method?: IdpOauth2LoginCodeChallengeMethod;
    action?: IdpOauth2AuthorizeAction;
    client_id: string;
    redirect_uri: string;
    scope: string;
    state: string;
    nonce: string;
    code_challenge?: string;
    login_hint?: string;
}

export enum IdpOauth2LoginMethod {
    ANONYMOUS = 'anonymous',
    EMAIL_PASSWORD = 'email-password',
    EMAIL_CODE = 'email-code',
    OPENID_ACM = 'openid-acm',
}
export enum IdpOauth2AuthorizeAction {
    REGISTER = 'register',
    SIGN_IN = 'sign-in',
}

export interface Oauth2RevokeTokenDto {
    client_id: string;
    client_secret?: string;
    token: string;
}

export interface Oauth2RefreshTokenResultDto {
    accessToken: string;
    refreshToken: string;
}

export enum IdpOauth2GrantType {
    AUTHORIZATION_CODE = 'authorization_code',
    REFRESH_TOKEN = 'refresh_token',
}

export interface Oauth2CreateTokenDto {
    client_id: string;
    client_secret?: string;
    code?: string;
    code_verifier?: string;
    grant_type: IdpOauth2GrantType;
    redirect_uri?: string;
    refresh_token?: string;
}

export interface AuthSession {
    accessToken: string;
    refreshToken: string;
    clientId: string;
}

export interface AccessToken {
    aud: string;
    azp: string;
    exp: number;
    iat: number;
    iss: string;
    scope: string;
    sub: string;
    token_use: string;
}

export interface DecodedAuthSession extends AuthSession {
    decodedAccessToken: AccessToken;
}

export interface Oauth2SignOutQueryDto {
    client_id: string;
    post_logout_redirect_uri: string;
    state?: string;
}
